import { Button } from 'antd';
import { useFormikContext } from 'formik';
import { type ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import './VendorLeadStepCondition.scss';

import AddPhotoIcon from 'src/assets/add-photo.svg?react';
import SdsFileIcon from 'src/assets/sds-file-icon.svg?react';
import { ValidationError } from 'src/components/base';
import type { IVendorLeadConditionStepFormValues } from 'src/components/vendors/VendorLeadTypes';
import { blobToBase64 } from 'src/helpers';
import { removeBase64Prefix } from 'src/helpers/removeBase64Prefix';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';
import type { IFilePayload } from 'src/types';

export function VendorLeadStepConditionSdsFilesList() {
  const fileRef = useRef<HTMLInputElement>(null);

  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext<IVendorLeadConditionStepFormValues>();

  const isSdsFilesDirty = useMemo(
    () => (values.sdsFiles.length === 0 ? false : values.sdsFiles.some((sdsFile) => sdsFile.uid == null)),
    [values.sdsFiles]
  );

  const { onEditing } = useContext(VendorsContext);

  const handleFilesSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const base64Files: IFilePayload[] = (
        await Promise.all(
          [...event.target.files].map(async (file) => {
            if (file) {
              const base64Image = await blobToBase64(file, true);

              if (base64Image) {
                return {
                  fileName: file.name,
                  contentType: file.type,
                  content: removeBase64Prefix(base64Image),
                };
              }
            }

            return null;
          })
        )
      ).filter(Boolean) as IFilePayload[];

      await setFieldValue('sdsFiles', [...base64Files, ...values.sdsFiles], true);
      await setFieldTouched('sdsFiles', true);
    },
    [setFieldValue, values.sdsFiles, setFieldTouched]
  );

  const handleFileRemove = useCallback(
    async (num: number) => {
      const newSdsFiles = values.sdsFiles.filter((_src, index) => index !== num);

      await setFieldValue('sdsFiles', newSdsFiles, true);
      await setFieldTouched('sdsFiles', true);
    },
    [values.sdsFiles, setFieldValue, setFieldTouched]
  );

  const handleImageUploadRequest = useCallback(() => fileRef.current?.click(), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    onEditing(isSdsFilesDirty);
  }, [isSdsFilesDirty]);

  return (
    <div className="vendor-lead-step-condition__sds-files">
      <input
        ref={fileRef}
        type="file"
        accept="
          text/csv,
          application/msword,
          application/vnd.openxmlformats-officedocument.wordprocessingml.document,
          application/vnd.ms-excel,
          application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
          application/pdf,
          image/bmp,
          image/gif,
          image/jpeg,
          image/jpeg,
          image/png,
          image/tiff,
          image/vnd.wap.wbmp,
          image/webp,
          image/svg+xml
        "
        hidden={true}
        multiple={true}
        onChange={handleFilesSelect}
      />
      <div>Please upload SDS of previously contained</div>
      <div className="vendor-lead-step-condition__controls">
        <div className="vendor-lead-step-condition__add-sds-file__button" onClick={handleImageUploadRequest}>
          <AddPhotoIcon style={{ fill: '#0082F9', width: 48, height: 48 }} />
          <span className="vendor-lead-step-condition__add-photo__text">Add SDS Files</span>
        </div>

        <div className="flex-3 flex column nowrap gap-small">
          {values.sdsFiles.length === 0 ? (
            <>
              <div className="flex row nowrap align-items-center gap-medium">
                <div className="vendor-lead-step-condition__info-icon">i</div>
                <div className="vendor-lead-step-condition__info-text">Click on add SDS files</div>
              </div>

              <div className="flex row nowrap align-items-center gap-medium">
                <div className="vendor-lead-step-condition__info-icon">i</div>
                <div className="vendor-lead-step-condition__info-text">
                  You can manage your uploaded files before finishing request
                </div>
              </div>
              <div className="flex row nowrap align-items-center gap-medium">
                <div className="vendor-lead-step-condition__info-icon">i</div>
                <div className="vendor-lead-step-condition__info-text">Maximum limit can be up to 100Mb</div>
              </div>
              {errors.sdsFiles && (
                <div className="flex row nowrap align-items-center gap-medium">
                  <div className="vendor-lead-step-condition__info-icon vendor-lead-step-condition__error-icon">i</div>
                  <div className="vendor-lead-step-condition__info-text">
                    <ValidationError
                      name="sdsFiles"
                      errorType="danger"
                      className="vendor-lead-step-condition__error-message"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            values.sdsFiles.map((sdsFile, num) => (
              <div key={sdsFile.fileName} className="flex row nowrap align-items-center gap-medium">
                <SdsFileIcon />
                <div className="vendor-lead-step-condition__info-text flex-1">{sdsFile.fileName}</div>
                {!sdsFile.uid && (
                  <Button type="link" onClick={() => handleFileRemove(num)}>
                    Delete
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
